import { Link } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { User, Compass, Users, MessageSquare, UserPlus, Calendar, Pencil, Briefcase, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { DashboardTabs } from "@/components/dashboard/DashboardTabs";
import type { User as UserType, Article, Event } from "@shared/schema";

function WelcomeSkeleton() {
  return (
    <Card data-testid="skeleton-welcome">
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Home() {
  const { user, isLoading: authLoading } = useAuth();

  const { data: dashboardStats, isLoading: statsLoading } = useQuery<{
    connections: number;
    pendingConnections: number;
    unreadMessages: number;
    unreadNotifications: number;
    creditBalance: number;
    monthlyCredits: number;
    approvedRoles: string[];
    pendingIntros: number;
    totalIntros: number;
  }>({
    queryKey: ["/api/dashboard/stats"],
    enabled: !!user,
  });

  const { data: connections, isLoading: connectionsLoading } = useQuery<any[]>({
    queryKey: ["/api/connections"],
    enabled: !!user,
  });

  const { data: articles, isLoading: articlesLoading } = useQuery<Article[]>({
    queryKey: ["/api/articles/featured"],
    enabled: !!user,
  });

  const { data: events, isLoading: eventsLoading } = useQuery<Event[]>({
    queryKey: ["/api/events/upcoming"],
    enabled: !!user,
  });

  const { data: leaders, isLoading: leadersLoading } = useQuery<UserType[]>({
    queryKey: ["/api/leaders/featured"],
    enabled: !!user,
  });

  const getInitials = (name?: string | null) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <section className="mb-6" data-testid="section-welcome">
          {authLoading ? (
            <WelcomeSkeleton />
          ) : (
            <Card data-testid="card-welcome">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16" data-testid="avatar-user">
                      <AvatarImage src={user?.profileImageUrl || undefined} alt={user?.firstName || "User"} />
                      <AvatarFallback className="text-lg">
                        {getInitials(`${user?.firstName || ""} ${user?.lastName || ""}`)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground" data-testid="text-greeting">
                        {greeting()}, {user?.firstName || "there"}
                      </h1>
                      {(user?.title || user?.company) && (
                        <p className="text-sm text-foreground font-medium" data-testid="text-title-company">
                          {user.title}{user.title && user.company && " at "}{user.company}
                        </p>
                      )}
                      {user?.role && (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="capitalize text-xs" data-testid="badge-user-role">
                            {user.role === "board_candidate" ? "Executive" : user.role}
                          </Badge>
                          {user?.primaryRole && user.primaryRole !== user.role && (
                            <Badge variant="outline" className="capitalize text-xs" data-testid="badge-primary-role">
                              {user.primaryRole}
                            </Badge>
                          )}
                        </div>
                      )}
                      <p className="text-muted-foreground" data-testid="text-headline">
                        {user?.headline || "Welcome to your dashboard"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="outline" asChild data-testid="button-view-profile">
                      <Link href="/profile">
                        <User className="mr-2 h-4 w-4" />
                        View Profile
                      </Link>
                    </Button>
                    <Button variant="ghost" size="icon" asChild data-testid="button-edit-profile-dashboard">
                      <Link href="/profile?edit=intro">
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button asChild data-testid="button-explore-network">
                      <Link href="/network">
                        <Compass className="mr-2 h-4 w-4" />
                        Explore Network
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        {user && !authLoading && (
          <section className="mb-6" data-testid="section-quick-stats">
            {statsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                        <div className="flex-1">
                          <Skeleton className="h-5 w-10 mb-1" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : dashboardStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link href="/networking">
                  <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-connections">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-connections">{dashboardStats.connections}</div>
                          <div className="text-xs text-muted-foreground">Connections</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                <Link href="/messages">
                  <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-messages">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <MessageSquare className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-messages">{dashboardStats.unreadMessages}</div>
                          <div className="text-xs text-muted-foreground">Messages</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                {user?.role === "founder" ? (
                  <Link href="/capital/deal-readiness">
                    <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-deal-readiness">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Briefcase className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-deal-readiness">{dashboardStats.pendingConnections}</div>
                            <div className="text-xs text-muted-foreground">Deal Readiness</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ) : user?.role === "investor" ? (
                  <Link href="/capital/dealroom">
                    <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-deal-flow">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <TrendingUp className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-deal-flow">{dashboardStats.pendingConnections}</div>
                            <div className="text-xs text-muted-foreground">Deal Flow</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ) : user?.role === "advisor" ? (
                  <Link href="/network/office-hours">
                    <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-office-hours">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Calendar className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-office-hours">{dashboardStats.pendingConnections}</div>
                            <div className="text-xs text-muted-foreground">Office Hours</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ) : (
                  <Link href="/messages?tab=requests">
                    <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-pending">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <UserPlus className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-pending">{dashboardStats.pendingConnections}</div>
                            <div className="text-xs text-muted-foreground">Pending</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                )}
                <Link href="/network/intro-requests">
                  <Card className="hover-elevate cursor-pointer h-full" data-testid="card-quick-intros">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <UserPlus className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-heading text-lg font-bold text-foreground" data-testid="text-quick-intros">{dashboardStats.pendingIntros}</div>
                          <div className="text-xs text-muted-foreground">Introductions</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            )}
          </section>
        )}

        {user && (
          <DashboardTabs
            user={user}
            dashboardStats={dashboardStats}
            articles={articles || []}
            events={events || []}
            leaders={leaders || []}
            connections={connections || []}
            statsLoading={statsLoading}
            articlesLoading={articlesLoading}
            eventsLoading={eventsLoading}
            leadersLoading={leadersLoading}
            connectionsLoading={connectionsLoading}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
